import React, { useState, useEffect, useRef } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import { addDoc, collection } from 'firebase/firestore';
import { useDarkMode } from '../context/DarkModeContext';
import { getTheme } from '../theme';

const MODES = {
  pomodoro: { label: 'Focus', duration: 25 * 60, color: '#c8973a' },
  short: { label: 'Short Break', duration: 5 * 60, color: '#2d7a4f' },
  long: { label: 'Long Break', duration: 15 * 60, color: '#b08a3e' },
};

const PomodoroTimer = ({ onClose }) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);
  const [mode, setMode] = useState('pomodoro');
  const [timeLeft, setTimeLeft] = useState(MODES.pomodoro.duration);
  const [running, setRunning] = useState(false);
  const [sessions, setSessions] = useState(0);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);
  const sessionStartRef = useRef(null);

  useEffect(() => {
    setTimeLeft(MODES[mode].duration);
    setRunning(false);
    clearInterval(intervalRef.current);
  }, [mode]);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            clearInterval(intervalRef.current);
            setRunning(false);
            if (mode === 'pomodoro') {
              setSessions(s => s + 1);
              if (user?.uid && sessionStartRef.current) {
                const durationSeconds = MODES.pomodoro.duration;
                addDoc(collection(db, 'studySessions'), {
                  userId: user.uid, page: 'pomodoro', startedAt: sessionStartRef.current,
                  endedAt: new Date().toISOString(), durationSeconds, date: new Date().toISOString().slice(0, 10),
                }).catch(() => {});
                sessionStartRef.current = null;
              }
            }
            // Play notification sound
            try { new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAA...').play(); } catch(e) {}
            // Browser notification
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              try { new Notification('⏰ Learnable', { body: mode === 'pomodoro' ? 'Focus session complete! Take a break.' : 'Break over — back to studying!' }); } catch (e) {}
            }
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running, mode, user]);

  useEffect(() => {
    if (running && mode === 'pomodoro' && !sessionStartRef.current) {
      sessionStartRef.current = new Date().toISOString();
    }
    if (!running) sessionStartRef.current = null;
  }, [running, mode]);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch (e) {}
    }
  }, []);

  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');
  const progress = 1 - timeLeft / MODES[mode].duration;
  const circumference = 2 * Math.PI * 54;
  const color = MODES[mode].color;

  return (
    <div style={{position:'fixed',bottom:isMobile?'72px':'80px',right:isMobile?'12px':'24px',left:isMobile?'12px':'auto',zIndex:60,backgroundColor:t.surface,border:`1px solid ${color}30`,borderRadius:'20px',padding:'20px',width:isMobile?'auto':'220px',maxWidth:isMobile?'none':'220px',boxShadow:'0 20px 60px rgba(0,0,0,0.5)',backdropFilter:'blur(16px)'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
        <span style={{fontSize:'12px',fontWeight:'700',color,textTransform:'uppercase',letterSpacing:'0.06em'}}>🍅 Pomodoro</span>
        <button onClick={onClose} aria-label="Close timer" style={{color:t.textSec,background:'none',border:'none',cursor:'pointer',fontSize:'16px',width:'32px',height:'32px',display:'flex',alignItems:'center',justifyContent:'center',borderRadius:'8px',flexShrink:0}}>✕</button>
      </div>

      {/* Mode tabs */}
      <div style={{display:'flex',gap:'3px',backgroundColor:t.surface2,padding:'3px',borderRadius:'8px',marginBottom:'16px'}}>
        {Object.entries(MODES).map(([key, m]) => (
          <button key={key} onClick={() => setMode(key)}
            style={{flex:1,padding:'4px 2px',borderRadius:'6px',fontSize:'9px',fontWeight:'600',border:'none',cursor:'pointer',
              backgroundColor:mode===key?m.color:'transparent',
              color:mode===key?'#f5f0e8':t.textSec}}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Circle timer */}
      <div style={{display:'flex',justifyContent:'center',marginBottom:'16px'}}>
        <svg width="120" height="120" style={{transform:'rotate(-90deg)'}}>
          <circle cx="60" cy="60" r="54" fill="none" stroke={t.border} strokeWidth="6" />
          <circle cx="60" cy="60" r="54" fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={circumference} strokeDashoffset={circumference * (1 - progress)}
            strokeLinecap="round" style={{transition:'stroke-dashoffset 0.5s ease',stroke:color}} />
        </svg>
        <div style={{position:'absolute',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',marginTop:'35px'}}>
          <span style={{fontSize:'26px',fontWeight:'800',color:t.text,fontVariantNumeric:'tabular-nums'}}>{mins}:{secs}</span>
          <span style={{fontSize:'10px',color:t.textSec,marginTop:'2px'}}>{MODES[mode].label}</span>
        </div>
      </div>

      {/* Controls */}
      <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
        <button onClick={() => setRunning(r => !r)}
          style={{flex:1,padding:'10px',borderRadius:'10px',fontSize:'13px',fontWeight:'700',background:`linear-gradient(135deg,${color},${color}cc)`,color:'#1a1a1a',border:'none',cursor:'pointer'}}>
          {running ? '⏸ Pause' : '▶ Start'}
        </button>
        <button onClick={() => { setRunning(false); setTimeLeft(MODES[mode].duration); }}
          style={{padding:'10px 12px',borderRadius:'10px',fontSize:'13px',backgroundColor:t.surface2,color:t.textSec,border:'1px solid rgba(200,151,58,0.1)',cursor:'pointer'}}>
          ↺
        </button>
      </div>

      {/* Session count */}
      <div style={{textAlign:'center'}}>
        <div style={{display:'flex',justifyContent:'center',gap:'4px',marginBottom:'4px'}}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{width:'8px',height:'8px',borderRadius:'50%',backgroundColor:i<(sessions%4)?color:'rgba(200,151,58,0.15)'}}></div>
          ))}
        </div>
        <div style={{fontSize:'10px',color:t.textSec}}>{sessions} session{sessions!==1?'s':''} completed today</div>
      </div>
    </div>
  );
};

export default PomodoroTimer;
